import {cn, Typography} from '@sollapay/ui';
import {Badge, Button, Icon, Input, Skeleton} from '@sollapay/ui/components';
import {normalizeEmail} from '@sollapay/utils';
import {useId, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {MemberAvatar, memberDisplayName} from '@/features/trust-accounts/members';

import {addChipEmails, getShareProblemKey, looksLikeEmail, splitEmailDraft} from '../utils';

import type {ShareChip, ShareSuggestion} from '../utils';
import type {ComponentProps, FC, KeyboardEvent} from 'react';

const CHIP_VARIANT: Record<ShareChip['kind'], ComponentProps<typeof Badge>['variant']> = {
  member: 'brand-subtle',
  guest: 'purple',
  problem: 'error'
};

export interface SharePeopleInputProps {
  chips: ShareChip[];
  draft: string;
  suggestions: ShareSuggestion[];
  onDraftChange: (draft: string) => void;
  onChipsChange: (emails: string[]) => void;
  onSubmit: () => void;
  isLoadingSuggestions?: boolean;
  hasError?: boolean;
}

/**
 * Turns people into chips: a suggestion picked from the list, or an address on Enter, comma or
 * semicolon (and space or blur once it has an `@`). Backspace on an empty field drops the last chip.
 */
export const SharePeopleInput: FC<SharePeopleInputProps> = props => {
  const {chips, suggestions, isLoadingSuggestions = false, hasError = false} = props;
  const {t} = useTranslation('trustAccounts');
  const listId = useId();
  const {emails, active, isListOpen, inputProps, pick, highlight} = useShareCombobox(props);
  const optionId = (email: string) => `${listId}-${email}`;

  return (
    <div data-slot="share-people-input" className="relative min-w-0 flex-1">
      <div
        className={cn(
          'flex min-h-10 flex-wrap items-center gap-1.5 rounded-md border border-border bg-background-surface px-2 py-1.5 shadow-xs transition-[color,box-shadow]',
          'focus-within:border-brand-accent focus-within:ring-1 focus-within:ring-brand-accent',
          hasError && 'border-border-error'
        )}
      >
        {chips.map(chip => (
          <ShareChipBadge
            key={chip.email}
            chip={chip}
            onRemove={() => props.onChipsChange(emails.filter(email => email !== chip.email))}
          />
        ))}
        <Input
          role="combobox"
          aria-label={t('share.add.inputLabel')}
          aria-autocomplete="list"
          aria-expanded={isListOpen}
          aria-controls={listId}
          aria-activedescendant={isListOpen && active ? optionId(active.email) : undefined}
          placeholder={t(chips.length ? 'share.add.placeholderMore' : 'share.add.placeholder')}
          autoComplete="off"
          className="h-7 min-w-36 flex-1 border-0 bg-transparent p-0 text-sm shadow-none focus-visible:shadow-none"
          {...inputProps}
        />
      </div>
      {isListOpen && (
        <ShareSuggestionList
          id={listId}
          suggestions={suggestions}
          isLoading={isLoadingSuggestions}
          active={active}
          optionId={optionId}
          onPick={pick}
          onHighlight={highlight}
        />
      )}
    </div>
  );
};

/**
 * The field's keyboard and list state. The option matching the typed address is active until the
 * arrow keys or the pointer choose another, so Enter never swaps a typed email for someone else.
 */
function useShareCombobox(props: SharePeopleInputProps) {
  const {chips, draft, suggestions, onDraftChange, onChipsChange, isLoadingSuggestions} = props;
  const [navigatedEmail, setNavigatedEmail] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const emails = chips.map(chip => chip.email);
  const pickable = suggestions.filter(isPickable);
  const typed = suggestions.find(suggestion => suggestion.email === normalizeEmail(draft));
  const navigated = pickable.find(suggestion => suggestion.email === navigatedEmail);
  const active = navigated ?? typed ?? pickable[0];
  const isListOpen = isFocused && !isDismissed && (isLoadingSuggestions || suggestions.length > 0);

  const addEmails = (incoming: string[], rest: string) => {
    if (incoming.length) onChipsChange(addChipEmails(emails, incoming));
    onDraftChange(rest);
    setNavigatedEmail(null);
    setIsDismissed(false);
  };

  const inputProps: ComponentProps<typeof Input> = {
    value: draft,
    onChange: event => {
      const {complete, rest} = splitEmailDraft(event.target.value);
      addEmails(complete, rest);
    },
    onKeyDown: event =>
      handleShareKey(event, {
        ...props,
        emails,
        pickable,
        active,
        isListOpen,
        addEmails,
        navigateTo: email => {
          setNavigatedEmail(email);
          setIsDismissed(false);
        },
        dismiss: () => setIsDismissed(true)
      }),
    onFocus: () => setIsFocused(true),
    onBlur: () => {
      setIsFocused(false);
      if (looksLikeEmail(draft)) addEmails([draft], '');
    }
  };

  return {
    emails,
    active,
    isListOpen,
    inputProps,
    pick: (email: string) => addEmails([email], ''),
    highlight: setNavigatedEmail
  };
}

interface ShareKeyContext extends SharePeopleInputProps {
  emails: string[];
  pickable: ShareSuggestion[];
  active: ShareSuggestion | undefined;
  isListOpen: boolean;
  addEmails: (incoming: string[], rest: string) => void;
  navigateTo: (email: string | null) => void;
  dismiss: () => void;
}

function handleShareKey(event: KeyboardEvent<HTMLInputElement>, context: ShareKeyContext): void {
  const {draft, emails, pickable, active, isListOpen, addEmails} = context;
  if (event.nativeEvent.isComposing) return;
  if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && pickable.length) {
    event.preventDefault();
    context.navigateTo(stepThrough(pickable, active, event.key === 'ArrowDown' ? 1 : -1));
  } else if (event.key === 'Escape' && isListOpen) {
    // Closes the list only; the panel around the field stays open.
    event.preventDefault();
    event.stopPropagation();
    context.dismiss();
  } else if (event.key === 'Enter') {
    event.preventDefault();
    if (isListOpen && active) addEmails([active.email], '');
    else if (draft.trim()) addEmails([draft], '');
    else context.onSubmit();
  } else if (event.key === 'Backspace' && !draft && emails.length) {
    context.onChipsChange(emails.slice(0, -1));
  }
}

function stepThrough(
  pickable: ShareSuggestion[],
  active: ShareSuggestion | undefined,
  step: 1 | -1
): string | null {
  const index = active ? pickable.indexOf(active) : -1;
  const next =
    index === -1 ? pickable.at(step === 1 ? 0 : -1) : pickable.at((index + step) % pickable.length);
  return next?.email ?? null;
}

function isPickable(suggestion: ShareSuggestion): boolean {
  return suggestion.kind === 'guest' || !suggestion.ineligibleReason;
}

interface ShareSuggestionListProps {
  id: string;
  suggestions: ShareSuggestion[];
  isLoading: boolean;
  active: ShareSuggestion | undefined;
  optionId: (email: string) => string;
  onPick: (email: string) => void;
  onHighlight: (email: string) => void;
}

const ShareSuggestionList: FC<ShareSuggestionListProps> = ({
  id,
  suggestions,
  isLoading,
  active,
  optionId,
  onPick,
  onHighlight
}) => {
  const {t} = useTranslation('trustAccounts');

  return (
    <>
      {isLoading && (
        <span role="status" className="sr-only">
          {t('share.add.loadingSuggestions')}
        </span>
      )}
      <ul
        id={id}
        role="listbox"
        aria-label={t('share.add.suggestions')}
        aria-busy={isLoading}
        className="absolute inset-x-0 top-full z-10 mt-1 flex flex-col rounded-lg border border-border bg-background-surface p-1 shadow-lg"
      >
        {isLoading
          ? Array.from({length: 3}, (_, index) => <ShareSuggestionSkeleton key={index} />)
          : suggestions.map(suggestion => (
              <ShareSuggestionOption
                key={suggestion.email}
                id={optionId(suggestion.email)}
                suggestion={suggestion}
                isActive={suggestion === active}
                onPick={() => onPick(suggestion.email)}
                onHighlight={() => onHighlight(suggestion.email)}
              />
            ))}
      </ul>
    </>
  );
};

const ShareSuggestionSkeleton: FC = () => (
  <li
    aria-hidden="true"
    data-slot="share-suggestion-skeleton"
    className="flex items-center gap-2.5 px-2 py-1.5"
  >
    <Skeleton className="size-6 shrink-0 rounded-full" />
    <span className="flex flex-1 flex-col gap-1">
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-3 w-44" />
    </span>
  </li>
);

interface ShareChipBadgeProps {
  chip: ShareChip;
  onRemove: () => void;
}

const ShareChipBadge: FC<ShareChipBadgeProps> = ({chip, onRemove}) => {
  const {t} = useTranslation('trustAccounts');
  const label = getChipLabel(chip);

  return (
    <Badge
      variant={CHIP_VARIANT[chip.kind]}
      size="sm"
      title={chip.kind === 'problem' ? t(getShareProblemKey(chip.problem)) : chip.email}
      className="gap-1 pe-0.5"
    >
      <span data-slot="label" dir={label === chip.email ? 'ltr' : undefined} className="truncate">
        {label}
      </span>
      <Button
        variant="tertiary"
        size="icon-xs"
        className="size-4.5 rounded-full text-current hover:bg-transparent hover:text-current"
        aria-label={t('share.add.removeChip', {name: label})}
        icon={<Icon name="x-close" className="size-3" aria-hidden />}
        onClick={onRemove}
      />
    </Badge>
  );
};

function getChipLabel(chip: ShareChip): string {
  if (chip.kind === 'member') return memberDisplayName(chip.person);
  if (chip.kind === 'problem') return chip.name ?? chip.email;
  return chip.email;
}

interface ShareSuggestionOptionProps {
  id: string;
  suggestion: ShareSuggestion;
  isActive: boolean;
  onPick: () => void;
  onHighlight: () => void;
}

const ShareSuggestionOption: FC<ShareSuggestionOptionProps> = ({
  id,
  suggestion,
  isActive,
  onPick,
  onHighlight
}) => {
  const {t} = useTranslation('trustAccounts');
  const isMember = suggestion.kind === 'member';
  const reason = isMember ? suggestion.ineligibleReason : null;
  const name = isMember ? memberDisplayName(suggestion.person) : suggestion.email;

  return (
    <li
      id={id}
      role="option"
      aria-selected={isActive}
      aria-disabled={!!reason}
      data-slot="share-suggestion"
      // Picks on mouse down and keeps focus in the field, so its blur can't commit the draft first.
      onMouseDown={event => {
        event.preventDefault();
        if (!reason) onPick();
      }}
      onMouseEnter={onHighlight}
      className={cn(
        'flex items-center gap-2.5 rounded-md px-2 py-1.5',
        reason ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        isActive && 'bg-background-selected'
      )}
    >
      <MemberAvatar
        user={isMember ? suggestion.person : {userId: suggestion.email, email: suggestion.email}}
        size="sm"
      />
      <span className="flex min-w-0 flex-1 flex-col">
        <Typography size="sm" weight="medium">
          <bdi>{name}</bdi>
        </Typography>
        {isMember && (
          <Typography size="xs" color="tertiary">
            {reason ? t(`members.ineligible.${reason}`) : <bdi>{suggestion.email}</bdi>}
          </Typography>
        )}
      </span>
      <Badge variant={isMember ? 'brand-subtle' : 'purple'} size="xs">
        {t(isMember ? 'share.add.tags.member' : 'share.add.tags.guest')}
      </Badge>
    </li>
  );
};
